import { useTheme } from "next-themes";
import { Sun, Moon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ThemeToggleProps {
  size?: "sm" | "md";
}

export function ThemeToggle({ size = "sm" }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  const containerClasses = size === "md"
    ? "w-14 h-7 p-0.5"
    : "w-12 h-6 p-0.5";

  const thumbClasses = size === "md"
    ? "w-6 h-6"
    : "w-5 h-5";

  const iconSize = size === "md" ? 14 : 12;

  const toggleTheme = () => {
    setTheme(isDark ? "light" : "dark");
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={toggleTheme}
            className={`${containerClasses} relative flex items-center rounded-full transition-colors duration-300 cursor-pointer ${
              isDark
                ? "bg-slate-600"
                : "bg-slate-300"
            }`}
            aria-label="Toggle theme"
          >
            <div
              className={`${thumbClasses} rounded-full flex items-center justify-center transition-all duration-300 shadow-md ${
                isDark
                  ? "translate-x-full bg-slate-700 text-yellow-400"
                  : "translate-x-0 bg-slate-700 text-slate-200"
              }`}
            >
              {isDark ? <Sun size={iconSize} /> : <Moon size={iconSize} />}
            </div>
            <span className="sr-only">Toggle theme</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="border-0 bg-white text-gray-900 dark:bg-gray-900 dark:text-white shadow-md">
          {isDark ? "Light Mode" : "Dark Mode"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
